import React, { useEffect, useState } from 'react';
import { Button, Col, Row, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import Section from './Section';
import Toggle from './Toggle';

const DEFAULT_SETTINGS = {
  predictionsPreGameEnabled: true,
  predictionsTeamChannelEnabled: true,
  predictionsAllPredictionsEnabled: true,
  fancamLiveEnabled: true,
  fancamChallengeEnabled: true
};

const ROWS = [
  {
    key: 'predictionsPreGameEnabled',
    label: 'Predictions – PreGame Tab',
    desc: 'The continuous predictor ticker under the score-prediction wheel'
  },
  {
    key: 'predictionsTeamChannelEnabled',
    label: 'Predictions – Team Channel',
    desc: 'The continuous predictor ticker under the Predictions module header'
  },
  {
    key: 'predictionsAllPredictionsEnabled',
    label: 'Predictions – All Predictions Tab',
    desc: 'The predictor ticker shown above the score-band breakdown'
  },
  {
    key: 'fancamLiveEnabled',
    label: 'FanCam Live',
    desc: 'The rotating "Powering the moment" indicator above the FanCam Live grid'
  },
  {
    key: 'fancamChallengeEnabled',
    label: 'FanCam Challenge',
    desc: 'The rotating "Powering the moment" indicator above the FanCam Challenge grid'
  }
];

const chunkPairs = (items) =>
  items.reduce((pairs, item, i) => {
    if (i % 2 === 0) {
      pairs.push([item]);
    } else {
      pairs[pairs.length - 1].push(item);
    }
    return pairs;
  }, []);

const ParticipationIndicatorTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await Api.get('/admin/participationIndicatorSettings/get');
      if (res?.data?.settings) {
        setSettings((prev) => ({ ...prev, ...res.data.settings }));
      }
    } catch (error) {
      console.error('Error fetching participation indicator settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await Api.put('/admin/participationIndicatorSettings/update', settings);
      alert('Settings updated successfully');
      fetchSettings();
    } catch (error) {
      console.error('Error updating participation indicator settings:', error);
      alert('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Col wid="100%" ht="100vh" centerAll noFlex>
        <Spinner2 />
      </Col>
    );
  }

  return (
    <Col start noFlex wid="100%" pad="24px" gap="32px">
      <Section
        title="Participation Indicator"
        description="Turn the participation ticker on or off per section — e.g. disable it during the off-season."
      >
        <Col gap="16px" wid="100%">
          {chunkPairs(ROWS).map((pair, i) => (
            <Row gap="16px" key={i}>
              {pair.map((row) => (
                <Col
                  key={row.key}
                  style={{
                    padding: '14px 16px',
                    borderRadius: '10px',
                    border: `1px solid ${theme.GREY_20 || '#eee'}`,
                    backgroundColor: '#fff'
                  }}
                >
                  <Row center between>
                    <Col style={{ flex: 1 }}>
                      <Text b2 bold marg="0">{row.label}</Text>
                      <Text c1 col={theme.GREY_60} marg="4px 0 0" style={{ fontSize: '11px' }}>
                        {row.desc}
                      </Text>
                    </Col>
                    <Toggle on={!!settings[row.key]} onToggle={() => handleToggle(row.key)} />
                  </Row>
                </Col>
              ))}
            </Row>
          ))}
        </Col>
      </Section>

      <Row marg="0 0 48px" centerAll>
        <Button
          text={saving ? 'Updating...' : 'Save Changes'}
          onClick={handleSave}
          bg={theme.PRIMARY}
          loading={saving}
          wid="200px"
        />
      </Row>
    </Col>
  );
};

export default ParticipationIndicatorTab;
